import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';

import { getClaimAction } from 'store/actions/propertyActions';
import { getClaim } from 'store/sagas/APIs/property';
import { fetchAllPaginated } from 'utils/exportPagination';
import { ClaimTable } from 'components/Tables/ClaimTable';
import { DatePickerInput, Dropdown, Loading } from '@leapeasy/ui-kit';
import { openDetails } from 'store/actions/uiActions';
import { failedNotif } from 'store/actions/notifActions';
import { buildBuildingBreadcrumbParent, withBreadcrumbParents } from 'utils/breadcrumbNavigation';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { ApplicationListActiveFilters } from 'components/ApplicationListActiveFilters/ApplicationListActiveFilters';
import {
  buildClaimListFilterChips,
  parseClaimListFilterChipId,
} from 'utils/propertyListActiveFilterChips';
import {
  CLAIM_LIST_URL_PARAM_KEYS,
  DEFAULT_CLAIM_LIST_EXTRA_FILTERS,
  areArraysEqual,
  areDateRangesEqual,
  normalizeArrayFilter,
  normalizeDateRangeFilter,
  parseClaimsExtraFilters,
  serializeClaimsFilters,
} from 'utils/claimListQueryFilters';
import {
  CLAIM_LIST_APP_TYPE_FILTER_OPTIONS,
  CLAIM_FILE_STATUS_FILTER_OPTIONS,
} from 'data/constants/common_constants';

const EMPTY_CLAIM_LIST_FILTER = {
  searchText: '',
  options: {},
  selectedFileStatus: [],
  selectedAppType: [],
  selectedLeaseDate: {
    startDate: null,
    endDate: null,
  },
};

export const ClaimTab = ({ building }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const previousBuildingIdRef = useRef(undefined);

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search Claims..',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: DEFAULT_CLAIM_LIST_EXTRA_FILTERS,
    parseExtraFilters: parseClaimsExtraFilters,
    serializeExtraFilters: serializeClaimsFilters,
  });

  const [selectedFileStatus, setSelectedFileStatus] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedFileStatus),
  );
  const [selectedAppType, setSelectedAppType] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedAppType),
  );
  const [selectedLeaseDate, setSelectedLeaseDate] = useState(() =>
    normalizeDateRangeFilter(extraFilters?.selectedLeaseDate),
  );
  const [selectedFileStatusDraft, setSelectedFileStatusDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedFileStatus),
  );
  const [selectedAppTypeDraft, setSelectedAppTypeDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedAppType),
  );
  const [selectedLeaseDateDraft, setSelectedLeaseDateDraft] = useState(() =>
    normalizeDateRangeFilter(extraFilters?.selectedLeaseDate),
  );
  const [fullExportDatasetTotal, setFullExportDatasetTotal] = useState(undefined);

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedFileStatus = normalizeArrayFilter(extraFilters.selectedFileStatus);
    const nextSelectedAppType = normalizeArrayFilter(extraFilters.selectedAppType);
    const nextSelectedLeaseDate = normalizeDateRangeFilter(extraFilters.selectedLeaseDate);
    setSelectedFileStatus((prev) =>
      areArraysEqual(prev, nextSelectedFileStatus) ? prev : nextSelectedFileStatus,
    );
    setSelectedAppType((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedLeaseDate((prev) =>
      areDateRangesEqual(prev, nextSelectedLeaseDate) ? prev : nextSelectedLeaseDate,
    );
    setSelectedFileStatusDraft((prev) =>
      areArraysEqual(prev, nextSelectedFileStatus) ? prev : nextSelectedFileStatus,
    );
    setSelectedAppTypeDraft((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedLeaseDateDraft((prev) =>
      areDateRangesEqual(prev, nextSelectedLeaseDate) ? prev : nextSelectedLeaseDate,
    );
  }, [extraFilters]);

  useLayoutEffect(() => {
    const id = building?.building_id;
    if (!id) return;
    if (previousBuildingIdRef.current !== id) {
      if (previousBuildingIdRef.current !== undefined) {
        const next = new URLSearchParams(searchParams);
        CLAIM_LIST_URL_PARAM_KEYS.forEach((key) => next.delete(key));
        setSearchParams(next, { replace: true });
      }
      previousBuildingIdRef.current = id;
    }
  }, [building?.building_id, searchParams, setSearchParams]);

  const handleDispatch = useCallback(() => {
    if (!building?.building_id) return;
    dispatch(
      getClaimAction({
        filter: {
          ...filter,
          selectedFileStatus,
          selectedAppType,
          selectedLeaseDate,
        },
        offset: pagination.pageNumber * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        sortOptions,
        buildingId: building.building_id,
      }),
    );
  }, [
    dispatch,
    building.building_id,
    filter,
    selectedFileStatus,
    selectedAppType,
    selectedLeaseDate,
    pagination.pageNumber,
    pagination.rowsPerPage,
    sortOptions,
  ]);

  useEffect(() => {
    handleDispatch();
  }, [handleDispatch]);

  const applyFunc = useCallback(() => {
    setSelectedFileStatus(selectedFileStatusDraft);
    setSelectedAppType(selectedAppTypeDraft);
    setSelectedLeaseDate(selectedLeaseDateDraft);
    setExtraFilters({
      selectedFileStatus: selectedFileStatusDraft,
      selectedAppType: selectedAppTypeDraft,
      selectedLeaseDate: selectedLeaseDateDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedFileStatusDraft,
    selectedAppTypeDraft,
    selectedLeaseDateDraft,
    setExtraFilters,
    setPagination,
  ]);

  const resetFunc = useCallback(() => {
    setSelectedFileStatus([]);
    setSelectedAppType([]);
    setSelectedLeaseDate({ startDate: null, endDate: null });
    setSelectedFileStatusDraft([]);
    setSelectedAppTypeDraft([]);
    setSelectedLeaseDateDraft({ startDate: null, endDate: null });
    setExtraFilters({ ...DEFAULT_CLAIM_LIST_EXTRA_FILTERS });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const onClickClaim = useCallback(
    (claim) => {
      if (!claim?.id) {
        dispatch(failedNotif('Claim detail ID is missing.'));
        return;
      }
      const buildingParent = buildBuildingBreadcrumbParent(building, building?.building_id);
      dispatch(
        openDetails({
          type: 'claim',
          data: claim,
        }),
      );
      navigate(`/property/claims/${claim.id}/detail`, {
        state: withBreadcrumbParents(buildingParent ? [buildingParent] : []),
      });
    },
    [dispatch, navigate, building],
  );

  const claims = useSelector((state) => state.getIn(['property', 'claim']));
  const claimsJSON = claims && claims.toJS();
  useClampListPagination({
    pagination,
    setPagination,
    totalItems: Number(claimsJSON?.total) || 0,
    isLoading: Boolean(claimsJSON?.isLoading),
  });

  useEffect(() => {
    if (!building?.building_id) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getClaim({
          filter: EMPTY_CLAIM_LIST_FILTER,
          offset: 0,
          limit: 1,
          sortOptions,
          buildingId: building.building_id,
        });
        if (!cancelled && response?.total != null) {
          setFullExportDatasetTotal(Number(response.total));
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sortOptions, building?.building_id]);

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(claimsJSON?.total) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getClaim({
            filter: {
              ...filter,
              selectedFileStatus,
              selectedAppType,
              selectedLeaseDate,
            },
            offset,
            limit,
            sortOptions,
            buildingId: building.building_id,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export filtered claims right now.');
    }
  }, [
    claimsJSON?.total,
    filter,
    selectedFileStatus,
    selectedAppType,
    selectedLeaseDate,
    sortOptions,
    building.building_id,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords = Number(fullExportDatasetTotal) || Number(claimsJSON?.total) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getClaim({
            filter: EMPTY_CLAIM_LIST_FILTER,
            offset,
            limit,
            sortOptions,
            buildingId: building.building_id,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export all claims right now.');
    }
  }, [fullExportDatasetTotal, claimsJSON?.total, sortOptions, building.building_id]);

  const APPTYPE = CLAIM_LIST_APP_TYPE_FILTER_OPTIONS;

  const CLAIM_FILE_STATUS = CLAIM_FILE_STATUS_FILTER_OPTIONS;

  const listFilterChips = useMemo(
    () =>
      buildClaimListFilterChips({
        selectedFileStatus,
        selectedAppType,
        selectedLeaseDate,
        fileStatusOptions: CLAIM_FILE_STATUS,
        appTypeOptions: APPTYPE,
      }),
    [selectedFileStatus, selectedAppType, selectedLeaseDate, CLAIM_FILE_STATUS, APPTYPE],
  );

  const handleRemoveClaimListFilterChip = useCallback(
    (chipId) => {
      const parsed = parseClaimListFilterChipId(chipId);
      if (!parsed) return;

      if (parsed.type === 'created') {
        const cleared = { startDate: null, endDate: null };
        setSelectedLeaseDate(cleared);
        setSelectedLeaseDateDraft(cleared);
        setExtraFilters((prev) => ({ ...prev, selectedLeaseDate: cleared }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'fileStatus') {
        const next = selectedFileStatus.filter((s) => s !== parsed.value);
        setSelectedFileStatus(next);
        setSelectedFileStatusDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedFileStatus: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'appType') {
        const next = selectedAppType.filter((s) => s !== parsed.value);
        setSelectedAppType(next);
        setSelectedAppTypeDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedAppType: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      }
    },
    [selectedFileStatus, selectedAppType, setExtraFilters, setPagination],
  );

  const listFilterActiveSummary = useMemo(() => {
    if (!listFilterChips.length) return null;
    return (
      <ApplicationListActiveFilters
        chips={listFilterChips}
        onRemoveChip={handleRemoveClaimListFilterChip}
        onClearAll={resetFunc}
      />
    );
  }, [listFilterChips, handleRemoveClaimListFilterChip, resetFunc]);

  return (
    <Box>
      {claims ? (
        <React.Fragment>
          <CustomBox>
            {claimsJSON.isLoading && (
              <LoadingEffect>
                <Loading size={'large'} />
              </LoadingEffect>
            )}
            <ClaimTable
              claims={claimsJSON.data}
              onRequestExportData={handleRequestExportData}
              onRequestFullExportData={handleRequestFullExportData}
              exportFullDatasetTotal={fullExportDatasetTotal}
              dropFilterActiveSummary={listFilterActiveSummary}
              dropFilterActiveCount={listFilterChips.length}
              filter={filter}
              dropFilter={{
                component: (
                  <DropFilterContainer>
                    <Dropdown
                      onChange={(e) => {
                        setSelectedFileStatusDraft(e.target.value);
                      }}
                      options={[...CLAIM_FILE_STATUS]}
                      value={selectedFileStatusDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder={'Select item'}
                      placeholderFlag={true}
                      label={'File Status'}
                      style={{ width: '100%' }}
                    />
                    <Dropdown
                      onChange={(e) => {
                        setSelectedAppTypeDraft(e.target.value);
                      }}
                      options={[...APPTYPE]}
                      value={selectedAppTypeDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder="Select item"
                      placeholderFlag={true}
                      label="Application Type"
                      style={{ width: '100%' }}
                    />
                    <RangeDatePickerContainer>
                      <DatePickerInput
                        label={'Created From'}
                        size="small"
                        value={selectedLeaseDateDraft.startDate}
                        maxDate={selectedLeaseDateDraft.endDate}
                        onChange={(date) => {
                          setSelectedLeaseDateDraft((prev) => ({
                            ...prev,
                            startDate: date.target.value,
                          }));
                        }}
                      />
                      <DatePickerInput
                        label={'Created To'}
                        size="small"
                        value={selectedLeaseDateDraft.endDate}
                        minDate={selectedLeaseDateDraft.startDate}
                        onChange={(date) => {
                          setSelectedLeaseDateDraft((prev) => ({
                            ...prev,
                            endDate: date.target.value,
                          }));
                        }}
                      />
                    </RangeDatePickerContainer>
                  </DropFilterContainer>
                ),
                applyFunc: applyFunc,
                resetFunc: resetFunc,
              }}
              onChangeFilter={setFilter}
              pagination={{
                ...pagination,
                totalItems: claimsJSON.total,
                totalPages: Math.ceil(claimsJSON.total / pagination.rowsPerPage),
              }}
              onChangePagination={setPagination}
              sortOptions={sortOptions}
              onChangeSort={setSortOptions}
              onClickClaim={onClickClaim}
            />
          </CustomBox>
        </React.Fragment>
      ) : (
        <Loading size={'large'} />
      )}
    </Box>
  );
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const RangeDatePickerContainer = styled.div`
  display: flex;
  max-width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  width: 100%;
  * {
    box-sizing: border-box;
  }
  & > div {
    width: 50% !important;
  }
  .ui-kit-input-body {
    width: 60%;
  }
`;

const CustomBox = styled.div`
  position: relative;
`;

const LoadingEffect = styled.div`
  position: absolute;
  top: 35%;
  left: 45%;
  z-index: 1;
`;
